import { FollowUser } from '../types';

let isFetchingFollowing = false;
let isFetchingFollowers = false;

/**
 * Fetch list of creators that the given user follows.
 */
export async function fetchFollowing(username: string): Promise<FollowUser[]> {
  const clean = username?.trim();
  if (!clean || typeof window === 'undefined') return [];

  if (isFetchingFollowing) return [];
  isFetchingFollowing = true;

  try {
    const res = await fetch(`/api/user/follows?username=${encodeURIComponent(clean)}&type=following`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && Array.isArray(data.following)) {
        return data.following;
      }
    }
  } catch (err) {
    console.warn('[followService] fetchFollowing notice:', err);
  } finally {
    isFetchingFollowing = false;
  }

  return [];
}

/**
 * Fetch list of performers who follow the given user.
 */
export async function fetchFollowers(username: string): Promise<FollowUser[]> {
  const clean = username?.trim();
  if (!clean || typeof window === 'undefined') return [];

  if (isFetchingFollowers) return [];
  isFetchingFollowers = true;

  try {
    const res = await fetch(`/api/user/follows?username=${encodeURIComponent(clean)}&type=followers`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && Array.isArray(data.followers)) {
        return data.followers;
      }
    }
  } catch (err) {
    console.warn('[followService] fetchFollowers notice:', err);
  } finally {
    isFetchingFollowers = false;
  }

  return [];
}

/**
 * Fast check: Does `follower` follow `target`?
 */
export async function checkIsFollowing(follower: string, target: string): Promise<boolean> {
  const f = follower?.trim();
  const t = target?.trim();
  if (!f || !t || typeof window === 'undefined') return false;
  if (f.toLowerCase() === t.toLowerCase()) return false;

  try {
    const res = await fetch(`/api/user/follows?follower=${encodeURIComponent(f)}&target=${encodeURIComponent(t)}`);
    if (res.ok) {
      const data = await res.json() as any;
      return Boolean(data.isFollowing);
    }
  } catch (err) {
    console.warn('[followService] checkIsFollowing notice:', err);
  }

  return false;
}

/**
 * 1-click asymmetric follow of a creator.
 */
export async function followUser(
  follower: string,
  target: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const f = follower?.trim();
  const t = target?.trim();

  if (!f || !t) {
    return { success: false, error: 'Both usernames are required' };
  }
  if (f.toLowerCase() === t.toLowerCase()) {
    return { success: false, error: 'You cannot follow yourself' };
  }

  try {
    const res = await fetch('/api/user/follows', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        followerUsername: f,
        targetUsername: t,
        action: 'follow'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to follow creator' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

/**
 * Unfollow a creator.
 */
export async function unfollowUser(
  follower: string,
  target: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const f = follower?.trim();
  const t = target?.trim();

  if (!f || !t) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/follows', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        followerUsername: f,
        targetUsername: t,
        action: 'unfollow'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to unfollow creator' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}
